import { useEffect, useState } from "react";
import axios from "axios";
import { LogIn, RefreshCw, Mail, Building2, Tag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Admin · Marketing for the Moment";
    const saved = sessionStorage.getItem("mftm_admin_pw");
    if (saved) {
      setPassword(saved);
      fetchItems(saved);
    }
  }, []);

  async function fetchItems(pw) {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { "X-Admin-Password": pw },
      });
      setItems(res.data);
      setAuthed(true);
      sessionStorage.setItem("mftm_admin_pw", pw);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Incorrect password." : "Could not load submissions.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }

  const login = (e) => {
    e.preventDefault();
    fetchItems(password);
  };

  const logout = () => {
    sessionStorage.removeItem("mftm_admin_pw");
    setAuthed(false);
    setItems([]);
    setPassword("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-md bg-parchment border border-line p-10" data-testid="admin-login">
          <p className="eyebrow">Admin</p>
          <h1 className="font-display text-4xl text-ink italic mt-3">Marketing for the Moment</h1>
          <p className="text-ink2 mt-2 text-sm">Sign in to view contact submissions.</p>
          <label className="eyebrow block mt-8 mb-2">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password"
            className="w-full bg-cream border border-line px-4 py-4 text-ink focus:outline-none focus:border-rust"
            autoFocus
          />
          {error && <p className="text-red-800 text-sm mt-3" data-testid="admin-error">{error}</p>}
          <button type="submit" data-testid="admin-login-btn" disabled={loading} className="btn-rust w-full justify-center mt-6 disabled:opacity-60">
            <LogIn size={16} /> {loading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-line bg-parchment">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div>
            <p className="eyebrow">Admin</p>
            <h1 className="font-display italic text-3xl text-ink">Notes from the site</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchItems(password)} className="btn-secondary" data-testid="admin-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} className="btn-primary" data-testid="admin-logout">Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-12 py-12">
        {loading && <p className="text-ink2">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="border border-dashed border-line bg-parchment p-12 text-center">
            <p className="font-display italic text-2xl text-ink">No notes yet.</p>
            <p className="text-ink2 mt-2 text-sm">Submissions from the contact form will appear here.</p>
          </div>
        )}
        <ul className="space-y-4">
          {items.map((it) => (
            <li key={it.id} className="border border-line bg-parchment p-6 md:p-8" data-testid={`submission-${it.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <h2 className="font-display italic text-2xl text-ink">{it.name}</h2>
                  <p className="text-ink2 text-sm mt-1 flex items-center gap-2"><Mail size={14} className="text-rust" />{it.email}</p>
                </div>
                <div className="text-right text-sm text-ink2">
                  <p>{new Date(it.created_at).toLocaleString()}</p>
                  <p className={`text-xs mt-1 ${it.emailed ? "text-rust" : "text-ink2"}`}>
                    {it.emailed ? "✓ Emailed to Andrea" : "Saved (email not configured)"}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink2">
                {it.business_name && <span className="flex items-center gap-2"><Building2 size={14} className="text-rust" />{it.business_name}</span>}
                {it.business_type && <span className="flex items-center gap-2"><Tag size={14} className="text-rust" />{it.business_type}</span>}
                {it.tier_interest && <span className="flex items-center gap-2 uppercase tracking-widest text-xs">{it.tier_interest}</span>}
              </div>
              <p className="mt-5 pt-5 border-t border-line text-ink text-[15px] leading-relaxed whitespace-pre-wrap">
                {it.message}
              </p>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
